import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetLanguagesQuery,
  useGetRelationsToPatientQuery,
} from "@/store/api/userEnums";
import {
  DateOfBirthField,
  LanguagesField,
  RelationToPatientField,
  SupportedByField,
} from "@/ui/components/profile/edit/OtherInfoFields";
import { Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { enumsToMultiSelectOptions } from "@/utils/api/enums.util";
import type { PermissionChecker } from "@/utils/profile/permissions.util";

const baseTestID = ProfileTestID.OtherInfo;

interface OtherInfoSectionProps extends UserOtherInfo {
  isAppUserSupport: boolean;
  onChange: (otherInfo: UserOtherInfo) => void;
  patientName?: string;
  supports?: string[];
  userPermissions: PermissionChecker;
}

const OtherInfoSection = ({
  dateOfBirth: initialDateOfBirth,
  isAppUserSupport,
  languages: initialLanguages,
  onChange,
  patientName,
  relationToPatient: initialRelationToPatient,
  supports,
  userPermissions,
}: OtherInfoSectionProps) => {
  const { track } = useAnalytics();
  const trackFieldPressIn = useCallback(
    (field: string) => {
      const fieldName = field.replace("Field", "");
      track(ProfileAnalyticsEvent.EditProfileFieldPressed(fieldName), {
        [AnalyticsFieldName.Component]: OtherInfoSection.name,
        [AnalyticsFieldName.Field]: fieldName,
        [AnalyticsFieldName.Source]: ModalPath.EditProfile,
      });
    },
    [track],
  );

  const [dateOfBirth, setDateOfBirth] = useState<string | undefined>(
    initialDateOfBirth,
  );
  const [relationToPatient, setRelationToPatient] = useState<
    string | undefined
  >(initialRelationToPatient);
  const [languages, setLanguages] = useState<string[] | undefined>(
    initialLanguages,
  );

  useEffect(() => {
    const otherInfo = {
      dateOfBirth,
      languages,
      relationToPatient,
    };
    onChange(otherInfo);
  }, [dateOfBirth, relationToPatient, languages, onChange]);

  const { data: languagesData } = useGetLanguagesQuery();
  const languageOptions = useMemo(
    () =>
      enumsToMultiSelectOptions({
        enumOptions: languagesData,
        valueField: "label",
      }),
    [languagesData],
  );

  const { data: relationsData } = useGetRelationsToPatientQuery();
  const relationOptions = useMemo(
    () =>
      enumsToMultiSelectOptions({
        enumOptions: relationsData,
        valueField: "label",
      }),
    [relationsData],
  );

  const RelationField = isAppUserSupport ? (
    <RelationToPatientField
      options={relationOptions}
      patientName={patientName}
      relationToPatient={relationToPatient}
      setRelationToPatient={setRelationToPatient}
      trackFieldPressIn={trackFieldPressIn}
      userPermissions={userPermissions}
    />
  ) : (
    <SupportedByField supports={supports} userPermissions={userPermissions} />
  );

  return (
    <VStack gap={12}>
      <Text testID={baseTestID} variant="h5">
        Other info
      </Text>
      <DateOfBirthField
        dateOfBirth={dateOfBirth}
        setDateOfBirth={setDateOfBirth}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      {RelationField}
      <LanguagesField
        languages={languages}
        options={languageOptions}
        setLanguages={setLanguages}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
    </VStack>
  );
};

export type { OtherInfoSectionProps };
export default OtherInfoSection;
